import { IChoiceGroupOption, Modal, ChoiceGroup, MessageBar, MessageBarType } from '@fluentui/react';
import React from 'react';
import { getLocaleText } from 'src/common/utilities/Utilities';
import MorayButton, { MorayButtonType } from '../moray/MorayButton';
import './AddressVerificationModal.scss';
import localeService from 'src/dataServices/LocaleService';

export interface IAddress {
  country: string;
  region: string; // state or province
  city: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
}

export interface IAddressVerificationModalProps {
  onSubmit: (ev?: React.MouseEvent<HTMLElement | HTMLButtonElement, MouseEvent>) => any; // existing (generic) submit hook
  onCancel: (ev?: React.MouseEvent<HTMLElement | HTMLButtonElement, MouseEvent>) => any;
  onApplyAddress?: (address: IAddress) => void;
  showAddressModal: boolean;
  originalAddress: IAddress;
  AVSResponseCode: number;
  suggested?: IAddress[]; // optional externally-provided suggestions
}

export default (props: IAddressVerificationModalProps) => {
  const [selectedAddress, setSelectedAddress] = React.useState<string | null>(null); // key string ('original' or index)

  const formatOriginalAddress = (addr: IAddress): string => {
    const line2 = addr.addressLine2 ? ` ${addr.addressLine2}` : '';
    const core = `${addr.addressLine1}${line2}`.trim();
    const locality = [addr.city, addr.region].filter(Boolean).join(', ');
    const postal = addr.postalCode ? addr.postalCode : '';
    const country = addr.country ? addr.country : '';
    return [core, locality, postal, country].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  };

  const originalAddressFormatted: string = formatOriginalAddress(props.originalAddress);

  const originalAddressOption: IChoiceGroupOption[] = [
    {
      key: 'original',
      text: originalAddressFormatted
    }
  ];

  const suggestedAddresses: IChoiceGroupOption[] =
    props.suggested && props.suggested.length > 0
      ? props.suggested.map((addr, idx) => ({
          key: `suggested-${idx}`,
          text: formatOriginalAddress(addr)
        }))
      : [];

  const handleSelectionChange = (key: string) => {
    setSelectedAddress(key);
  };

  const getSelectedAddress = (): IAddress | null => {
    if (selectedAddress === 'original') {
      return props.originalAddress;
    } else if (selectedAddress?.startsWith('suggested-')) {
      const idx = parseInt(selectedAddress.split('-')[1], 10);
      if (!isNaN(idx) && props.suggested && props.suggested[idx]) {
        return props.suggested[idx];
      }
    }
    return null;
  };

  const messageBarType = props.AVSResponseCode === 429 ? MessageBarType.warning : MessageBarType.error;
  const errorMessage =
    props.AVSResponseCode === 429
      ? localeService.getText('ProfileV2', 'AddressVerificationModalTooManyRequestsMessage')
      : localeService.getText('ProfileV2', 'AddressVerificationModalErrorMessage');

  return (
    <Modal
      isBlocking={true}
      isOpen={props.showAddressModal}
      onDismiss={props.onCancel}
      containerClassName="address-modal"
    >
      <div className="address-modal__content">
        <h1 id="addressModalTitle">{getLocaleText('ProfileV2', 'AddressVerificationModalTitle')}</h1>
        <h2 id="addressModalOriginal">{getLocaleText('ProfileV2', 'AddressVerificationModalOriginalAddressLabel')}</h2>
        <ChoiceGroup
          options={originalAddressOption}
          selectedKey={selectedAddress}
          onChange={(ev, option) => handleSelectionChange(option?.key ?? '')}
          ariaLabelledBy="addressModalOriginal"
        />
        <br />
        <h2 id="addressModalSuggested">
          {getLocaleText('ProfileV2', 'AddressVerificationModalSuggestedAddressesLabel')}
        </h2>
        {props.AVSResponseCode === 200 ? (
          suggestedAddresses.length > 0 ? (
            <ChoiceGroup
              options={suggestedAddresses}
              selectedKey={selectedAddress}
              onChange={(ev, option) => handleSelectionChange(option?.key ?? '')}
              ariaLabelledBy="addressModalSuggested"
            />
          ) : (
            <p>{getLocaleText('ProfileV2', 'AddressVerificationModalNoSuggestedAddresses')}</p>
          )
        ) : (
          <MessageBar messageBarType={messageBarType} isMultiline={true}>
            {errorMessage}
          </MessageBar>
        )}
        <div className="address-actions">
          <MorayButton
            text={localeService.getText('ProfileV2', 'Cancel')}
            type={MorayButtonType.Outline}
            onClick={() => {
              setSelectedAddress(null);
              props.onCancel();
            }}
          />
          <MorayButton
            text={localeService.getText('ProfileV2', 'ContinueButton')}
            type={MorayButtonType.Active}
            onClick={() => {
              const chosen = getSelectedAddress();
              if (chosen && props.onApplyAddress) {
                props.onApplyAddress(chosen);
              }
              props.onSubmit();
              setSelectedAddress(null);
            }}
            disabled={selectedAddress === null}
          />
        </div>
      </div>
    </Modal>
  );
};
